import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { extractXlsxTextBrowser } from "@/lib/xlsx-utils";
import { supabase } from "@/integrations/supabase/client";
import { ImportMethodSelector } from "@/components/import/ImportMethodSelector";
import { ImportMappingReview } from "@/components/import/ImportMappingReview";
import { ImportProgress } from "@/components/import/ImportProgress";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import { cleanCPF } from "@/lib/cpf-validator";
import { useSubscriptionStatus } from "@/hooks/useSubscriptionStatus";
import { SubscriptionModal } from "@/components/payment/SubscriptionModal";

type Step = 1 | 2 | 3;

interface MappedPatient {
  full_name: string;
  cpf: string;
  data_nascimento?: string | null;
  sexo?: string | null;
  email?: string | null;
  celular?: string | null;
  confidence: {
    full_name: number;
    cpf: number;
    data_nascimento: number;
    sexo: number;
    email: number;
    celular: number;
  };
}

interface ImportResult {
  success: number;
  errors: Array<{ patient: string; error: string }>;
  warnings: string[];
}

export default function PacientesImportar() {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>(1);
  const [mappedPatients, setMappedPatients] = useState<MappedPatient[]>([]);
  const [overallConfidence, setOverallConfidence] = useState(0);
  const [aiWarnings, setAiWarnings] = useState<string[]>([]);
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState<ImportResult>({
    success: 0,
    errors: [],
    warnings: []
  });

  const subscriptionStatus = useSubscriptionStatus();
  const [showPlanBlocker, setShowPlanBlocker] = useState(false);
  const [requiredPlan, setRequiredPlan] = useState<{ code: string; name: string; price: number } | null>(null);

  const processWithAI = async (rawData: string) => {
    try {
      toast.loading("Processando dados com IA...");

      const { data, error } = await supabase.functions.invoke(
        'import-patients-ai-mapping',
        { body: { rawData } }
      );

      toast.dismiss();

      if (error) throw error;

      if (!data.patients || data.patients.length === 0) {
        toast.error("Nenhum paciente válido foi detectado nos dados fornecidos");
        return;
      }

      setMappedPatients(data.patients);
      setOverallConfidence(data.overall_confidence || 0);
      setAiWarnings(data.warnings || []);
      setStep(2);

      toast.success(`${data.patients.length} paciente${data.patients.length !== 1 ? 's detectados' : ' detectado'} pela IA`);
    } catch (error: any) {
      console.error('Erro ao processar com IA:', error);
      toast.error("Erro ao processar dados: " + (error.message || "Erro desconhecido"));
    }
  };

  const handleFileSelected = async (file: File) => {
    try {
      toast.loading("Extraindo dados do arquivo...");
      const extractedText = await extractXlsxTextBrowser(file);
      toast.dismiss();

      if (!extractedText || extractedText.trim().length === 0) {
        toast.error("Arquivo vazio ou formato inválido");
        return;
      }

      await processWithAI(extractedText);
    } catch (error: any) {
      toast.dismiss();
      console.error('Erro ao processar arquivo:', error);
      toast.error("Erro ao ler arquivo: " + (error.message || "Erro desconhecido"));
    }
  };

  const handleTextPasted = async (text: string) => {
    await processWithAI(text);
  };

  const handleConfirmImport = async () => {
    // Verificar limite do plano antes de importar
    const totalAfterImport = subscriptionStatus.patientsCount + mappedPatients.length;
    const maxAllowed = subscriptionStatus.currentPlan?.max_patients || 10;

    if (subscriptionStatus.currentPlan?.max_patients !== null && totalAfterImport > maxAllowed) {
      // Calcular plano necessário
      const { data: plans } = await supabase
        .from('subscription_plans')
        .select('*')
        .order('min_patients', { ascending: true });

      const neededPlan = plans?.find(p =>
        p.max_patients === null || totalAfterImport <= p.max_patients
      );

      if (neededPlan && neededPlan.code !== 'free') {
        setRequiredPlan({
          code: neededPlan.code,
          name: neededPlan.name,
          price: neededPlan.price_cents,
        });
        setShowPlanBlocker(true);
        return;
      }

      toast.error(`Seu plano suporta apenas ${maxAllowed} pacientes. Total após importação: ${totalAfterImport}`);
      return;
    }

    setImporting(true);
    setStep(3);
    setProgress(0);

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      toast.error("Usuário não autenticado");
      setImporting(false);
      return;
    }

    const importResults: ImportResult = {
      success: 0,
      errors: [],
      warnings: []
    };

    for (let i = 0; i < mappedPatients.length; i++) {
      const patient = mappedPatients[i];
      setProgress(i + 1);

      try {
        const cpfDigits = cleanCPF(patient.cpf || "");
        if (cpfDigits.length !== 11) {
          importResults.errors.push({
            patient: patient.full_name,
            error: "CPF inválido ou ausente",
          });
          continue;
        }

        const { data: existing } = await supabase
          .from("patients_onboarding")
          .select("id")
          .eq("cpf", cpfDigits)
          .eq("owner_id", user.id)
          .maybeSingle();

        if (existing) {
          importResults.warnings.push(`CPF ${cpfDigits} (${patient.full_name}) já cadastrado`);
          continue;
        }

        // Inserir paciente
        const { error } = await supabase
          .from("patients_onboarding")
          .insert({
            full_name: patient.full_name,
            cpf: cpfDigits,
            data_nascimento: patient.data_nascimento || null,
            sexo: patient.sexo || null,
            email: patient.email || null,
            celular: patient.celular || null,
            owner_id: user.id,
            status: "active"
          });

        if (error) throw error;
        importResults.success++;
      } catch (error: any) {
        console.error(`Erro ao importar ${patient.full_name}:`, error);
        importResults.errors.push({
          patient: patient.full_name,
          error: error.message || "Erro desconhecido"
        });
      }
    }

    setResults(importResults);
    setImporting(false);

    if (importResults.success > 0) {
      toast.success(`${importResults.success} paciente${importResults.success !== 1 ? 's importados' : ' importado'} com sucesso!`);
    }

    if (importResults.errors.length > 0) {
      toast.error(`${importResults.errors.length} erro${importResults.errors.length !== 1 ? 's' : ''} durante importação`);
    }
  };

  const handleBack = () => {
    if (step === 2) {
      setStep(1);
      setMappedPatients([]);
      setAiWarnings([]);
    } else if (step === 3) {
      setStep(1);
      setMappedPatients([]);
      setResults({ success: 0, errors: [], warnings: [] });
      setProgress(0);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/app/pacientes")}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1">
          <h1 className="text-3xl font-bold tracking-tight">Importar Pacientes</h1>
          <p className="text-muted-foreground">
            Use IA para importar múltiplos pacientes de forma inteligente
          </p>
        </div>
      </div>

      {/* Stepper */}
      <div className="flex items-center justify-center gap-2">
        <div className="flex items-center gap-2">
          <div className={cn(
            "w-8 h-8 rounded-full flex items-center justify-center font-medium transition-colors",
            step >= 1 ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
          )}>
            1
          </div>
          <span className={cn(
            "text-sm font-medium transition-colors",
            step >= 1 ? "text-foreground" : "text-muted-foreground"
          )}>
            Método
          </span>
        </div>

        <Separator className="w-12" />

        <div className="flex items-center gap-2">
          <div className={cn(
            "w-8 h-8 rounded-full flex items-center justify-center font-medium transition-colors",
            step >= 2 ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
          )}>
            2
          </div>
          <span className={cn(
            "text-sm font-medium transition-colors",
            step >= 2 ? "text-foreground" : "text-muted-foreground"
          )}>
            Revisar
          </span>
        </div>

        <Separator className="w-12" />

        <div className="flex items-center gap-2">
          <div className={cn(
            "w-8 h-8 rounded-full flex items-center justify-center font-medium transition-colors",
            step >= 3 ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
          )}>
            3
          </div>
          <span className={cn(
            "text-sm font-medium transition-colors",
            step >= 3 ? "text-foreground" : "text-muted-foreground"
          )}>
            Importar
          </span>
        </div>
      </div>

      {/* Content */}
      {step === 1 && (
        <ImportMethodSelector
          onFileSelected={handleFileSelected}
          onTextPasted={handleTextPasted}
        />
      )}

      {step === 2 && (
        <ImportMappingReview
          patients={mappedPatients}
          overallConfidence={overallConfidence}
          warnings={aiWarnings}
          onConfirm={handleConfirmImport}
          onBack={handleBack}
        />
      )}

      {step === 3 && (
        <ImportProgress
          importing={importing}
          progress={progress}
          total={mappedPatients.length}
          results={results}
          onBack={handleBack}
        />
      )}

      {/* Modal de Upgrade com SubscriptionModal */}
      {requiredPlan && (
        <SubscriptionModal
          open={showPlanBlocker}
          onOpenChange={(open) => {
            setShowPlanBlocker(open);
            if (!open) setStep(2);
          }}
          planCode={requiredPlan.code}
          planName={requiredPlan.name}
          planPrice={requiredPlan.price}
          onSuccess={() => {
            setShowPlanBlocker(false);
            window.location.reload();
          }}
        />
      )}
    </div>
  );
}
